// hooks/market/useContactSeller.ts
//
// Bouton "Contacter le vendeur" sur une boutique/produit Market, quand
// aucun numéro WhatsApp Business n'est configuré. Réutilise le système
// de messagerie interne EXISTANT (même endpoint /messages, même action
// getOrCreateConversation que messages.tsx → openPrivateChat), plutôt
// que de créer un système de messagerie séparé pour Market.
//
// Après création/récupération de la conversation, navigue vers l'écran
// Messages avec des query params que messages.tsx doit lire au montage
// (via useLocalSearchParams) pour basculer automatiquement en mode chat
// — voir l'extrait fourni pour messages.tsx.
//
// PATCH V13 : handleMessages exige user_id ET access_token dans le body
// (voir backend/handleMessages.ts, tout début du handler REST — sans
// user_id la requête échoue systématiquement avec 400 "user_id et
// access_token requis", avant même d'atteindre getOrCreateConversation).
// user_id est récupéré depuis AsyncStorage sous SESSION_KEY, exactement
// comme messages.tsx le fait au montage (initSession) — même clé, même
// structure Supabase (session.user.id), même chaînage optionnel. Si
// aucune session n'est trouvée ou que session.user.id est absent
// (utilisateur non connecté / session locale corrompue), on n'appelle
// pas le backend : on renvoie directement error='not_authenticated', à
// afficher comme message visuel invitant à se connecter.

import { useState, useCallback } from 'react';
import { useRouter } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

const SESSION_KEY = 'harmonia_session';

type SellerInfo = {
  id: string;
  nom: string;
  prenom: string;
};

export function useContactSeller() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const contactSeller = useCallback(async (seller: SellerInfo) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      // Même clé, même stockage que messages.tsx — l'ID utilisateur n'est
      // pas dérivé du token ici, il est lu directement depuis la session
      // locale, à l'identique (structure Supabase : session.user.id).
      const sessionRaw = await AsyncStorage.getItem(SESSION_KEY);
      if (!sessionRaw) {
        setError('not_authenticated');
        return;
      }

      let userId: string | null = null;
      try {
        const session = JSON.parse(sessionRaw);
        userId = session?.user?.id ?? null;
      } catch {
        userId = null;
      }

      if (!userId) {
        setError('not_authenticated');
        return;
      }

      // Même endpoint /messages, même action que openPrivateChat dans
      // messages.tsx — voir handleMessages côté backend (déjà existant).
      // user_id est requis par le dispatcher handleMessages en plus de
      // access_token (voir patch V13 ci-dessus).
      const res = await fetch(`${API_BASE_URL}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'getOrCreateConversation',
          access_token: token,
          user_id:      userId,
          friend_id:    seller.id,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de contacter le vendeur');
        return;
      }

      // Navigation vers Messages avec les infos nécessaires pour ouvrir
      // directement le chat — messages.tsx lit ces params au montage.
      const params = new URLSearchParams({
        openConversationId: data.conversation_id,
        otherUserId:        seller.id,
        otherUserNom:       seller.nom,
        otherUserPrenom:    seller.prenom,
      });

      router.push(`/(tabs)/messages?${params.toString()}`);
    } catch (err) {
      console.error('[useContactSeller]', err);
      setError('network_error');
    } finally {
      setLoading(false);
    }
  }, [router]);

  return { contactSeller, loading, error };
}
